/**
 * Selector de sucursal del superadmin, siempre visible en la cabecera: elegir una sucursal para operar,
 * cambiar a otra o volver a la consola global. Tomar contexto sigue pasando por el diálogo con motivo (ADR-0003).
 */
import type { SucursalDTO } from '@mg/shared';
import { useQuery } from '@tanstack/react-query';
import { Building2, Check, ChevronDown, Globe } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { DialogoOperarSucursal } from '@/componentes/DialogoOperarSucursal.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/componentes/ui/dropdown-menu.tsx';
import { get, mensajeDeError } from '@/lib/api.ts';
import { useAuth } from '@/lib/auth.tsx';
import { atenderCambioDeSucursal, type DestinoSucursal } from '@/lib/cambio-sucursal.ts';
import { claves } from '@/lib/query.ts';

export function SelectorSucursal() {
  const { usuario, salirContexto } = useAuth();
  const navigate = useNavigate();
  // Lista completa aunque ya opere una sucursal (`todas`): sin eso, estando en una tienda solo salía esa y no se podía cambiar a otra
  const sucursales = useQuery({ queryKey: [...claves.sucursales, 'selector'], queryFn: () => get<SucursalDTO[]>('/sucursales', { todas: 1 }), enabled: usuario?.rol === 'superadmin', staleTime: 5 * 60_000 });
  const [objetivo, setObjetivo] = useState<DestinoSucursal | null>(null);
  const enContexto = Boolean(usuario?.sucursal_id);

  const elegir = async (s: DestinoSucursal) => {
    if (s.id === usuario?.sucursal_id) return;
    try {
      if (enContexto) await salirContexto();
      setObjetivo({ id: s.id, nombre: s.nombre });
    } catch (e) {
      toast.error('No se pudo cambiar de sucursal', { description: mensajeDeError(e) });
    }
  };
  // Otra pantalla puede pedir el cambio (p. ej. para ir a cerrar una caja que quedó abierta en otra sucursal)
  useEffect(() => {
    atenderCambioDeSucursal((d) => void elegir(d));
    return () => atenderCambioDeSucursal(null);
  });
  if (!usuario || usuario.rol !== 'superadmin') return null;
  const activas = (sucursales.data ?? []).filter((s) => s.activa);
  const volverGlobal = async () => {
    try {
      await salirContexto();
      navigate('/consolidado');
    } catch (e) {
      toast.error('No se pudo salir del contexto', { description: mensajeDeError(e) });
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant={enContexto ? 'outline' : 'default'} size="sm" className="gap-1.5" data-testid="selector-sucursal" title="Sucursal que operas como superadmin">
            {enContexto ? <Building2 className="h-4 w-4" /> : <Globe className="h-4 w-4" />}
            {enContexto ? usuario.sucursal_nombre : 'Elegir sucursal'}
            <ChevronDown className="h-3.5 w-3.5 opacity-70" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64">
          <DropdownMenuLabel>{enContexto ? 'Cambiar de sucursal' : 'Operar una sucursal'}</DropdownMenuLabel>
          {activas.map((s) => (
            <DropdownMenuItem key={s.id} onSelect={() => void elegir(s)} data-testid={`selector-sucursal-${s.codigo}`} className="flex items-center justify-between gap-2">
              <span>
                {s.nombre} <span className="text-[11px] text-muted-foreground">· {s.codigo}</span>
              </span>
              {s.id === usuario.sucursal_id ? <Check className="h-4 w-4 text-mg-verde" /> : null}
            </DropdownMenuItem>
          ))}
          {activas.length === 0 ? <DropdownMenuItem disabled>Sin sucursales activas</DropdownMenuItem> : null}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => void (enContexto ? volverGlobal() : navigate('/consolidado'))} data-testid="selector-sucursal-global" className="flex items-center gap-2">
            <Globe className="h-4 w-4" /> {enContexto ? 'Volver a la consola global' : 'Ver consola global'}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <DialogoOperarSucursal sucursal={objetivo} cerrar={() => setObjetivo(null)} />
    </>
  );
}
